import { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  activeEntrySelector,
  sheetsSelector,
  entriesSelector,
} from 'apps/formsCreationKit/store/selectors/entities'
import { elementsActions } from 'apps/studio/store/reducers/entities/elements'
const { updateElementStyles } = elementsActions
import {
  VerticalAlignment as verticalAlignmentSetting,
  HorizontalAlignment as horizontalAlignmentSetting,
} from 'apps/studio/components/settings/fields/Base/design'
import { SettingsFieldSelect } from 'apps/studio/components/settings/inputs/SettingsFieldSelect'
import {
  SettingsElementStyleSelect,
  SettingsFontFamilySelect,
} from 'apps/studio/components/settings/inputs/SettingsElementStyleSelect'
import {
  activePageSelector,
  layoutsSelector,
  visibleStyledElementsWorkspaceSelector,
} from 'apps/studio/store/selectors/pdf'
import { currentZoomSelector } from 'apps/studio/store/selectors/studioSettings'
import { pdfPageMetaByPageNumberSelector } from 'apps/studio/store/selectors/pdfPageMeta'
import { GCDSButton } from '@gocanvas/gcds/components'
import {
  DeleteIcon,
  FontBoldIcon,
  FontItalicIcon,
  UnderlineIcon,
  DuplicateIcon,
} from '@gocanvas/gcds/icons'
import {
  getDeleteElementAction,
  getDuplicateElementAction,
} from 'apps/studio/lib/utils/entities'

const elementRectToStyles = elementRect => {
  return {
    width: `${elementRect.width}px`,
    height: `${elementRect.height}px`,
    left: `${elementRect.left}px`,
    top: `${elementRect.top}px`,
  }
}

const calculateToolbarPosition = (toolbar, activeElement) => {
  // Get viewport dimensions to determine midline
  const elementRect = activeElement.getBoundingClientRect()
  const { left, top, height, width } = elementRect
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  // Check for CSS anchor positioning support
  if (CSS.supports('anchor-name', '--test')) {
    return {
      positionArea: true,
      elementRect: elementRectToStyles(elementRect),
    }
  } else {
    const { width: toolBarWidth, height: toolBarHeight } =
      toolbar.getBoundingClientRect()
    const toolbarOverlap = (width - toolBarWidth) / 2
    let leftPosition = left
    let topPosition = top

    // Horizontal position bias
    leftPosition = left + toolbarOverlap

    // Vertical position - default to below element, only go above if no room below
    topPosition = top + height

    // Check if toolbar would go off bottom of screen
    if (topPosition + (toolBarHeight + 10) > viewportHeight) {
      topPosition = top - toolBarHeight - 10
    }

    // stay in bounds
    leftPosition = Math.max(
      0,
      Math.min(leftPosition, viewportWidth - toolBarWidth)
    )
    topPosition = Math.max(
      0,
      Math.min(topPosition, viewportHeight - toolBarHeight)
    )

    return {
      left: `${leftPosition}px`,
      top: `${topPosition}px`,
      elementRect: elementRectToStyles(elementRect),
    }
  }
}

const ElementStyleButton = ({
  entryGuid,
  property,
  Icon,
  activeValue,
  nextValue,
  styles,
}) => {
  const dispatch = useDispatch()
  const active = styles[property] === activeValue

  return (
    <GCDSButton
      className={`button--toggle workspace-element--toolbar--${property} ${
        active ? 'is-active' : ''
      }`}
      LeadingIcon={() => (
        <Icon className="gcds--icon icon--svg icon--leading" />
      )}
      onClick={() => {
        dispatch(
          updateElementStyles({ entry_guid: entryGuid, [property]: nextValue })
        )
      }}
    />
  )
}

export const ElementToolbar = ({ element, entry }) => {
  const dispatch = useDispatch()
  const { entry_guid, styles } = element
  const toolbarRef = useRef()
  const page = useSelector(activePageSelector)
  const { page_number: pageNumber } = page
  const activeEntry = useSelector(activeEntrySelector)
  const { sheet_id: sheetId, keyField: isKeyField } = activeEntry
  const sheets = useSelector(sheetsSelector)
  const { section_id: sectionId } =
    sheets.find(sheet => sheet.id === sheetId) || {}
  const { section_id: parentSectionId } =
    sheets.find(sheet => sheet.multi_section === sectionId) || {}
  const layouts = useSelector(layoutsSelector)
  // When looking for the matching layout, need to match on parent sheet's section id when the element is part of the child screen in a table
  const sectionIdToMatchOn = parentSectionId || sectionId
  const layout =
    layouts.find(layout => layout.section_id === sectionIdToMatchOn) || {}
  const { layout_type, columns = [] } = layout
  const isTable = layout_type === 'table'
  const allEntries = useSelector(entriesSelector)
  const allStyledElements = useSelector(visibleStyledElementsWorkspaceSelector)
  const currentZoom = useSelector(currentZoomSelector)
  const pdfPageMeta =
    useSelector(pdfPageMetaByPageNumberSelector(pageNumber)) || {}
  const { scaleFactor = 1 } = pdfPageMeta
  const [positionStyles, setPositionStyles] = useState({
    topPosition: 0,
    leftPosition: 0,
  })

  useEffect(() => {
    const activeElement = document.getElementById(
      `page-element--wrapper-${entry_guid}`
    )

    const updatePosition = () => {
      if (!activeElement || !toolbarRef.current) {
        return
      }

      const positionStyles = calculateToolbarPosition(
        toolbarRef.current,
        activeElement
      )

      setPositionStyles(positionStyles)
    }

    updatePosition()
  }, [element, currentZoom, scaleFactor, page])

  const { elementRect, positionArea, top, left } = positionStyles

  return (
    <>
      <div
        className="workspace-element--toolbar-anchor"
        style={{
          ...elementRect,
          anchorName: `--pdf--anchor--${entry_guid}`,
        }}
      ></div>
      <div
        ref={toolbarRef}
        className="gcds--toolbar workspace-element--toolbar"
        onClick={e => e.stopPropagation()}
        style={{
          ...(positionArea
            ? {}
            : {
                position: 'fixed',
                left,
                top,
              }),
          ...(positionStyles.positionArea
            ? {
                positionAnchor: `--pdf--anchor--${entry_guid}`,
              }
            : {}),
        }}
      >
        <div className="toolbar--item">
          <SettingsFieldSelect
            id={`toolbar-field-select-${entry_guid}`}
            data={activeEntry}
            iconOnly={true}
            selectProps={{ isSearchable: false }}
            forceAlignRight={true}
          />
        </div>
        <div className="toolbar--item workspace-element--toolbar-item--font">
          <SettingsFontFamilySelect entryGuid={entry_guid} data={styles} />
        </div>
        <div className="toolbar--item">
          <SettingsElementStyleSelect
            entryGuid={entry_guid}
            choices={verticalAlignmentSetting.choices}
            iconOnly={true}
            data={styles}
            property="verticalAlignment"
            selectProps={{ isSearchable: false }}
          />
        </div>
        <div className="toolbar--item">
          <SettingsElementStyleSelect
            entryGuid={entry_guid}
            choices={horizontalAlignmentSetting.choices}
            iconOnly={true}
            data={styles}
            property="horizontalAlignment"
            selectProps={{ isSearchable: false }}
          />
        </div>
        <div className="toolbar--item">
          <ElementStyleButton
            entryGuid={entry_guid}
            Icon={FontBoldIcon}
            activeValue={700}
            nextValue={styles.fontWeight === 700 ? 400 : 700}
            property="fontWeight"
            styles={styles}
          />
        </div>
        <div className="toolbar--item">
          <ElementStyleButton
            entryGuid={entry_guid}
            Icon={FontItalicIcon}
            activeValue="italic"
            property="fontStyle"
            nextValue={styles.fontStyle === 'italic' ? 'normal' : 'italic'}
            styles={styles}
          />
        </div>
        <div className="toolbar--item">
          <ElementStyleButton
            entryGuid={entry_guid}
            Icon={UnderlineIcon}
            activeValue="underline"
            nextValue={
              styles.textDecoration === 'underline' ? 'none' : 'underline'
            }
            property="textDecoration"
            styles={styles}
          />
        </div>
        {!isKeyField && (
          <div className="toolbar--item">
            <button
              className="gcds--button button--toggle workspace-element--toolbar--duplicate"
              onClick={e => {
                e.stopPropagation()
                const action = getDuplicateElementAction({
                  element,
                  layout,
                  sheets,
                  pdfPageMeta,
                  entry,
                  entries: allEntries,
                  elements: allStyledElements,
                })
                dispatch(action)
              }}
            >
              <DuplicateIcon />
            </button>
          </div>
        )}
        {!isKeyField && !(isTable && columns.length <= 2) && (
          <div className="toolbar--item">
            <button
              className="gcds--button button--toggle workspace-element--toolbar--delete"
              onClick={e => {
                e.stopPropagation()
                const action = getDeleteElementAction({
                  element,
                  layout,
                  sheets,
                  entries: allEntries,
                  elements: allStyledElements,
                })
                dispatch(action)
              }}
            >
              <DeleteIcon />
            </button>
          </div>
        )}
      </div>
    </>
  )
}
